/* eslint-disable react/no-multi-comp */
import React, {
    type ComponentProps,
    type CSSProperties,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AsyncFnReturn } from 'react-use/lib/useAsyncFn';

import { MobileCodeEditorTestIO } from '@edu-frontend/modules/src/shared/ui/MobileCodeEditorTestIO';
import cx from 'classnames';
import { v4 as uuid } from 'uuid';
import { EditorView } from '@codemirror/view';

import { useDeepCompareMemoize } from '../../../utils/hooks/useDeepCompareMemoize';
import { getExperimentFlags } from '../../experiments/selectors';
import { CodingProblemGptPopup } from '../__gpt-popup/coding-problem__gpt-popup';
import { InputSwitch } from '../__input-switch/coding-problem__input-switch';
import { Stdio } from '../__stdio/coding-problem__stdio';
import { useCodingPropsSelector } from '../context';
import { useCodingStore } from '../state';
import type { ProblemCodingAttempt, ProblemCodingMarkup } from '../types';
import { catchAll } from '../utils';
import { useAppContext } from '../../app-factory/context';

// @ts-ignore
import styles from './coding-problem__tests.module.scss';

type CodingTestsProps = {
    runCodeState: AsyncFnReturn[0];
    isMobile?: boolean;
    style?: CSSProperties;
    resizeKey?: number;
};

const ANIMATION_DURATION_IN_MS = 1000;

export function CodingTests(props: CodingTestsProps) {
    const { runCodeState, isMobile, style, resizeKey } = props;

    const problemId = useCodingPropsSelector((p) => p.problem.id);

    const selectedTest = useCodingStore((s) => s.selectedTest);
    const setHelp = useCodingStore((s) => s.setHelp);
    const lastSubmit = useCodingStore((s) => s.lastSubmit)[problemId];
    const setCustomTestInput = useCodingStore((s) => s.setCustomTestInput);

    const attempt = useCodingPropsSelector((p) => p.attempt);
    const markers = attempt?.markers;
    const markup = useCodingPropsSelector((p) => p.problem.markup);

    const inputs = markup.inputs;
    const attemptMemo = useDeepCompareMemoize(attempt?.markers);
    const attemptKey = useMemo(uuid, [attemptMemo]);
    const readOnly = useCodingPropsSelector((p) => p.readOnly === true);
    const shouldShowCorrectAnswers = useCodingPropsSelector((p) => p.showCorrectAnswers);
    const teacherMode = useCodingPropsSelector((p) => p.teacherMode);

    // Эффект следит за переходом между проблемами и тестами внутри них,
    // а так же за отправкой решения
    useEffect(() => {
        if (!lastSubmit || lastSubmit === 'submit-answer') {
            const code = markers?.user_answer?.code;
            const error =
                markers?.result[selectedTest]?.check_details.exception_traceback || markers?.exception_traceback;
            const input = markers?.result[selectedTest]?.check_details.input?.data.join('\n') || '';
            const currentCodeHelp = code && error && { problemId, code, error, input };

            setHelp(problemId, currentCodeHelp || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problemId, selectedTest, markers, setHelp, lastSubmit]);

    useLayoutEffect(() => {
        const input = inputs[selectedTest]?.data.join('\n') || '';
        setCustomTestInput(input);
    }, [inputs, selectedTest, setCustomTestInput]);

    const [attention, setAttention] = useState(false);
    const [previousRunId, setPreviousRunId] = useState<string>();

    useEffect(() => {
        if (runCodeState.value?.runId) {
            setPreviousRunId(runCodeState.value.runId);
        }
    }, [previousRunId, runCodeState.value]);

    useEffect(() => {
        if (previousRunId) {
            setAttention(true);
            const timer = setTimeout(() => {
                setAttention(false);
            }, ANIMATION_DURATION_IN_MS);

            return () => clearTimeout(timer);
        }

        return () => {};
    }, [previousRunId]);

    return (
        <div
            className={cx(styles.tests, isMobile ? styles.tests_mobile : styles.tests_desktop, {
                [styles.tests_teacher]: teacherMode,
            })}
            style={style}
        >
            {!isMobile && inputs.length > 1 && <InputSwitch label="problemCoding.tests" view="desktop" />}
            <StdioListMemo
                attemptMarkers={attemptMemo}
                attention={attention}
                isMobile={isMobile}
                key={`${selectedTest}:${attemptKey}:${shouldShowCorrectAnswers}`}
                markup={markup}
                problemId={problemId}
                readOnly={readOnly}
                resizeKey={resizeKey}
                runCodeState={runCodeState}
                selected={selectedTest}
            />
        </div>
    );
}

function StdioList({
    attemptMarkers,
    markup,
    problemId,
    selected,
    readOnly,
    resizeKey,
    runCodeState,
    isMobile,
    attention,
}: {
    attemptMarkers: ProblemCodingAttempt['markers'];
    markup: ProblemCodingMarkup;
    problemId: number;
    selected: number;
    resizeKey?: number;
    readOnly?: boolean;

    /** (Локальный запуск) Результат запуска кода */
    runCodeState: AsyncFnReturn[0];
    isMobile?: boolean;
    attention: boolean;
}) {
    const help = useCodingStore((s) => s.help)[problemId];
    const isFocusOnInput = useCodingStore((store) => store.isFocusOnInput);
    const setIsFocusOnInput = useCodingStore((store) => store.setIsFocusOnInput);
    const customTestInput = useCodingStore((store) => store.customTestInput);
    const setCustomTestInput = useCodingStore((store) => store.setCustomTestInput);

    const { uatraits } = useAppContext();
    const isTouch = uatraits.isTouch;

    /** Отобразить правильную программу */
    const shouldShowCorrectAnswers = useCodingPropsSelector((p) => p.showCorrectAnswers);

    /** Ожидаемый вывод программы */
    const correctOutput = markup.inputs[selected]?.correct_answer;
    const hasCorrectOutput = typeof correctOutput === 'string';
    const showCorrectOutput = hasCorrectOutput && Boolean(markup.options?.show_correct_stdout || attemptMarkers);

    /** Надо ли показывать полную ошибку вместо краткой */
    const isFullPythonErrorEnabled = (
        useSelector(getExperimentFlags) as { showFullCodeErrorInProblemInformatics?: boolean } | null
    )?.showFullCodeErrorInProblemInformatics;

    /** (Отправка решения) */
    const pupilAnswer = catchAll(
        () => attemptMarkers?.result[selected]?.check_details?.check_results?.[0].pupil_answer
    );

    /** (Локальный запуск) Программа завершилась с ошибкой */
    const isRunCodeException = runCodeState.value?.status === 'exception';
    const intl = useIntl();

    /** (Локальный запуск) Краткое описание ошибки выполнения программы, а в случае ее отсутствия – вывод программы */
    const runCodeOutput = useMemo(() => {
        const message: Record<string, string> | undefined = runCodeState.value;

        switch (message?.exception_type) {
            case 'SyntaxError':
                return intl.formatMessage({ id: 'problemCoding.verdict.SyntaxError' });
            case 'IndentationError':
                return intl.formatMessage({ id: 'problemCoding.verdict.IndentationError' });
            case 'timeout_waiting_payload_execution':
                return intl.formatMessage({ id: 'problemCoding.verdict.Timeout' });
            default:
                return message?.exception_message || message?.exception_type || message?.output;
        }
    }, [intl, runCodeState.value]);

    /** То, что мы отображаем в окне ответа */
    const output = useMemo(() => {
        if (shouldShowCorrectAnswers) {
            if (typeof correctOutput !== 'undefined') {
                return `${correctOutput}`;
            }
            return '';
        }

        if (isFullPythonErrorEnabled && help?.error) {
            return help?.error;
        }

        if (typeof runCodeOutput === 'string') {
            return runCodeOutput;
        }
        return pupilAnswer;
    }, [correctOutput, help?.error, isFullPythonErrorEnabled, pupilAnswer, runCodeOutput, shouldShowCorrectAnswers]);

    const status = useMemo(() => {
        const hasOutput = typeof output === 'string';

        if (!hasOutput) {
            return 'default';
        }

        const isCorrectOutput = output.trim() === String(correctOutput).trim();

        if (!isRunCodeException && isCorrectOutput) {
            return 'correct';
        }

        return 'incorrect';
    }, [correctOutput, isRunCodeException, output]);

    const testInputRef: ComponentProps<typeof MobileCodeEditorTestIO>['editorRef'] = useRef(null);

    const blurEditorFocus = useCallback(() => {
        testInputRef.current?.view?.contentDOM.blur();

        if (window) {
            window.removeEventListener('touchmove', blurEditorFocus);
        }
    }, []);

    useEffect(() => {
        if (isFocusOnInput && window) {
            window.addEventListener('touchmove', blurEditorFocus);
        }

        return isFocusOnInput && window
            ? () => {
                  window.removeEventListener('touchmove', blurEditorFocus);
              }
            : undefined;
    }, [blurEditorFocus, isFocusOnInput]);

    const outputRef = useRef<HTMLDivElement>(null);

    const scrollToActiveLine = useCallback(() => {
        if (testInputRef && testInputRef.current) {
            const editorView = testInputRef.current.view;
            editorView?.dispatch({
                effects: EditorView.scrollIntoView(editorView.state.selection.main.head, {
                    y: 'center',
                }),
            });
        }
    }, []);

    useEffect(() => {
        // Когда фокусируемся на инпуте, скроллим к активной строке
        // Нужно чтобы активная строка не улетела за пределы экрана и подскролл произошел корректно, т.к. вьюпорт становится уже,
        // когда открывается мобильная клавиатура
        if (isFocusOnInput) {
            window.visualViewport?.addEventListener('resize', scrollToActiveLine);
        }

        return () => {
            window.visualViewport?.removeEventListener('resize', scrollToActiveLine);
        };
    }, [isFocusOnInput, scrollToActiveLine]);

    const resetToValue = markup.inputs[selected]?.data.join('\n');

    if (isMobile) {
        return (
            <>
                {Boolean(markup.inputs[selected]?.data?.length) && (
                    <MobileCodeEditorTestIO
                        className={styles['tests__stdio-list-mobile-item']}
                        editable={!readOnly}
                        editorRef={testInputRef}
                        headerTitle={intl.formatMessage({ id: 'problemCoding.test.input' })}
                        onBlur={isTouch ? () => setIsFocusOnInput(false) : undefined}
                        onChange={(value) => {
                            setCustomTestInput(value);
                            scrollToActiveLine();
                        }}
                        onFocus={
                            isTouch
                                ? () => {
                                      setIsFocusOnInput(true);
                                  }
                                : undefined
                        }
                        onReset={() => setCustomTestInput(resetToValue)}
                        value={customTestInput}
                    />
                )}

                <MobileCodeEditorTestIO
                    className={styles['tests__stdio-list-mobile-item']}
                    editable={false}
                    errorMessage={isRunCodeException ? output : undefined}
                    headerTitle={intl.formatMessage({ id: 'problemCoding.test.output' })}
                    status={status}
                    value={
                        (isRunCodeException ? undefined : output) ||
                        intl.formatMessage({ id: 'codingProblem.mobileEditor.outputPlaceholder' })
                    }
                />
                {showCorrectOutput && (
                    <MobileCodeEditorTestIO
                        className={styles['tests__stdio-list-mobile-item']}
                        editable={false}
                        headerTitle={intl.formatMessage({ id: 'problemCoding.test.correctOutput' })}
                        status={status}
                        value={(showCorrectOutput && `${correctOutput}`) || ''}
                    />
                )}
            </>
        );
    }

    return (
        <div className={styles['tests__stdio-list']}>
            <CodingProblemGptPopup anchor={outputRef} problemId={problemId} resizeKey={resizeKey} />

            {Boolean(markup.inputs[selected]?.data?.length) && (
                <Stdio
                    className={styles['tests__stdio-list-item']}
                    onChange={setCustomTestInput}
                    readOnly={readOnly}
                    resetToValue={resetToValue}
                    title={<FormattedMessage id="problemCoding.test.input" />}
                    value={customTestInput}
                />
            )}

            <Stdio
                attention={attention}
                className={styles['tests__stdio-list-item']}
                isException={isRunCodeException}
                readOnly
                title={<FormattedMessage id="problemCoding.test.output" />}
                titleRef={outputRef}
                value={output}
            />
            <Stdio
                className={cx(styles['tests__stdio-list-item'], {
                    [styles['tests__stdio-list-item_hidden']]: !showCorrectOutput,
                })}
                readOnly
                title={<FormattedMessage id="problemCoding.test.correctOutput" />}
                value={(showCorrectOutput && `${correctOutput}`) || ''}
            />
        </div>
    );
}

const StdioListMemo = React.memo(StdioList);
